import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next'
import { FormFields } from '~/components/FormFields';
import Layout from '~/components/Layout'
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import { useNavigate, useParams } from 'react-router-dom';
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery';
import dayjs from 'dayjs'
import { Form, Grid, Tab, TabPane } from 'semantic-ui-react';
import { useAuth } from '~/routes/components/AuthProvider';
import { DateTimeInput } from 'semantic-ui-calendar-react';
import LanguageTab from '~/pages/Dairy/MilkFixedPrice/LanguageTab';
import { formatNumber, getDateWithTime, getMaxDate, getMinDate, handleErrors } from '~/utils/helpers';
import { getError } from '~/components/FormFields/util';
import styled from 'styled-components';
import { FatAndProteinQuery } from '~/QueryHooks/FatAndProteinQuery';
import FatAndProteinService from '~/api/services/fatAndProtein';
import { FAT_PROTEIN_SLUG } from '~/Constants';

const StyledErrorLabel = styled.div`
white-space: normal;
background: #fff !important;
border: 1px solid #e0b4b4 !important;
color: #9f3a38 !important;
`

const CreateEditFatAndProteinPrice: React.FC = () => {
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const [isBusy, setIsBusy] = useState(false)
  const { id } = useParams()
  const [activeIndex, setActiveIndex] = useState(0)
  const navigate = useNavigate();
  const { t } = useTranslation()
  const languageLocal = languages.find(language => language.id === languageId)

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { isSubmitted, dirtyFields }
  } = useForm({
    mode: 'onSubmit'
  })

  const watchLanguages = watch('languages')

  const {
    isLoading,
    data: milkFixedCreateOptions,
    refetch: fetchOptions
  } = FatAndProteinQuery.getMilkFixexPriceOptions()

  const {
    data: farmerGroupList
  } = FarmerGroupQuery.getFarmersList(FAT_PROTEIN_SLUG)

  useEffect(() => {
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (id && farmerGroupList) {
      fetchTradingSlotData()
    } else {
      const values = getValues('') || {}
      values.languages = values?.languages || []
      milkFixedCreateOptions?.languages.map((language) => {
        const index = values?.languages.findIndex(languageText => languageText.id === language.id)
        if (index < 0) {
          values.languages.push({
            id: language.id,
            description: ''
          })
        }
      })
      reset(values)
    }
  }, [farmerGroupList, milkFixedCreateOptions])

  async function fetchTradingSlotData() {
    setIsBusy(true)
    try {
      const response = await FatAndProteinService.editTradingSlot(id)
      response.data.farmerGroups = farmerGroupList.map(farmerGroup => response.data.farmerGroups.find(id => id === farmerGroup.id) ? farmerGroup.id : undefined)
      response.data.dateFrom = dayjs(new Date(response.data.dateFrom)).format('DD.MM.YYYY HH:mm')
      response.data.dateTo = dayjs(new Date(response.data.dateTo)).format('DD.MM.YYYY HH:mm')
      milkFixedCreateOptions?.languages?.map((language) => {
        const index = response.data.languages.findIndex(languageText => languageText.id === language.id)
        if (index < 0) {
          response.data.languages.push({
            id: language.id,
            description: '',
            order: language.order
          })
        } else {
          response.data.languages[index].order = language.order
        }
      })
      response.data.languages?.sort((a, b) => a.order - b.order)
      reset(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const watchDateFrom = watch('dateFrom')
  const watchDateTo = watch('dateTo')

  function removeIfNotBase64(languageArray) {
    languageArray.forEach(language => {
      if (!language.file) {
        language.filename = null
        language.originalFilename = null
      }
    })
    return languageArray
  }

  function validateSameMonth(dateFrom, dateTo) {
    const fromMonth = dayjs(dateFrom).format('MMMM');
    const toMonth = dayjs(dateTo).format('MMMM');

    return fromMonth === toMonth;
  }

  async function onSubmit(data) {
    data.dateFrom = convertDate(watchDateFrom)
    data.dateTo = convertDate(watchDateTo)
    data.farmerGroups = data.farmerGroups.filter(farmerGroup => !!farmerGroup)
    //remove attachment if it is not base64 (api issue)
    data.languages = removeIfNotBase64(data.languages);
    if (!validateSameMonth(data.dateFrom, data.dateTo)) {
      notify(t('Date from and date to must be in the same month'), 'error');
      return
    }
    setIsBusy(true)
    let response
    try {
      if (id) {
        response = await FatAndProteinService.updateTradingSlot(id, data)
        notify(t('Fat & Protein trading slot updated'), 'success');
      } else {
        response = await FatAndProteinService.createTradingSlot(data)
        notify(t('Fat & Protein trading slot created'), 'success');
      }
      setIsBusy(false)
      navigate(RouteTypes.FAT_PROTEIN_PRICE_SHOW.replace(':id', id ? id : response.data.tradingSlotId))
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function convertDate(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    const time = yearAndTime?.split(' ')[1]
    return (`${year}-${month}-${day} ${time}`)
  }

  useEffect(() => {
    if (watchDateFrom && watchDateTo) {
      const dateFrom = getDateWithTime(watchDateFrom)
      const dateTo = getDateWithTime(watchDateTo)
      if (dateFrom && dateTo && dayjs(dateFrom).diff(dayjs(dateTo)) > 0) {
        setValue('dateTo', dayjs(dateFrom).format('DD.MM.YYYY HH:mm'))
      }
    }
  }, [watchDateFrom])

  function renderLanguageTabs() {
    const panes = milkFixedCreateOptions?.languages?.map((language, index) => ({
      menuItem: milkFixedCreateOptions?.languages[index]?.label || null,
      component: <LanguageTab
        index={index}
        language={language}
        control={control}
        getValues={getValues}
        setValue={setValue}
        languages={milkFixedCreateOptions?.languages}
      />
    }))

    return <Tab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeIndex}
      renderActiveOnly={false}
      // @ts-ignore: semantic type error
      onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
      panes={panes.map((pane, index) => {
        return {
          //@ts-ignore
          menuItem: pane.menuItem,
          pane: <TabPane
          // eslint-disable-next-line
            children={pane.component}
            active={index === activeIndex}
            style={{ border: 'none' }}
            key={pane.menuItem}
          />
        }
      })}
    />
  }

  function renderForm() {
    return <>
      <Grid columns={2}>
        <Grid.Row>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Method for trading slot')}
              selectOnBlur={false}
              rules={{
                required: {
                  value: true,
                  message: t('Please select method')
                }
              }}
              placeholder={t('Please select method')}
              options={milkFixedCreateOptions?.tradingSlotMethods.map(tradingSlot => ({ value: tradingSlot.key, text: tradingSlot.label }))}
              name={'confirmMethod'}
            />
            <FormFields.Select
              control={control}
              selectOnBlur={false}
              placeholder={t('Please select volume type')}
              label={t('Trading volume type')}
              options={milkFixedCreateOptions?.tradingVolumeTypes.map(tradingVolume => ({ value: tradingVolume.id, text: tradingVolume.name }))}
              rules={{
                required: {
                  value: true,
                  message: t('Please select volume type')
                }
              }}
              name={'tradingVolumeTypeId'}
            />
            <FormFields.Select
              multiple
              search
              rules={{
                required: {
                  value: true,
                  message: t('Please select farmer group')
                }
              }}
              defaultValue={[]}
              label={t('Farmer groups')}
              placeholder={t('Farmer groups')}
              selectOnBlur={false}
              selection
              control={control}
              name={'farmerGroups'}
              options={farmerGroupList?.map(farmer => ({ value: farmer.id, text: `${farmer.name} (${formatNumber(farmer.amountPercent)}%)` }))}
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Select
              control={control}
              selectOnBlur={false}
              label={t('Amount offering types')}
              rules={{
                required: {
                  value: true,
                  message: t('Please select offering type')
                }
              }}
              placeholder={t('Please select oferring type')}
              options={milkFixedCreateOptions?.amountOfferingTypes.map(amountOffer => ({ value: amountOffer.key, text: amountOffer.label }))}
              name={'amountOfferingType'}
            />
            <Form.Group widths={'equal'}>
              <Controller
                control={control}
                name={'dateFrom'}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter date from')
                  },
                  validate: () => {
                    const dateFrom = getDateWithTime(watchDateFrom)
                    const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
                    if (dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0) {
                      return t('Please select a date ahead of the current date.')
                    }
                    return true
                  }
                }}
                render={({ field, fieldState }) => {
                  const dateFrom = watchDateFrom ? getDateWithTime(watchDateFrom) : ''
                  const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
                  return <Form.Field
                    required
                    error={!watchDateFrom && isSubmitted ? getError(fieldState) : dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 ? true : false}
                  >
                    <label>{t('Date from')}</label>
                    <DateTimeInput
                      name='dateTime'
                      placeholder={t('Date from')}
                      minDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
                      closable
                      markColor={'blue'}
                      localization={languageLocal.code}
                      closeOnMouseLeave={false}
                      dateTimeFormat={'DD.MM.YYYY HH:mm'}
                      className={'datePicker'}
                      value={watchDateFrom}
                      iconPosition='left'
                      onChange={(event, { name, value }) => setValue('dateFrom', value)}
                    />
                    {!watchDateFrom && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter date from')}</StyledErrorLabel>}
                    {dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 &&
                      <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the current date.')}</StyledErrorLabel>}
                  </Form.Field>

                }}
              />
              <Controller
                control={control}
                name={'dateTo'}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter date to')
                  }
                }}
                render={({ field, fieldState }) => {
                  return <Form.Field
                    required
                    error={!watchDateTo && isSubmitted ? getError(fieldState) : false}
                  >
                    <label>{t('Date to')}</label>
                    <DateTimeInput
                      name='dateTime'
                      className={'datePicker'}
                      placeholder={t('Date to')}
                      closable
                      markColor={'blue'}
                      initialDate={getMinDate(watchDateFrom)}
                      minDate={getMinDate(watchDateFrom)}
                      maxDate={getMaxDate(watchDateFrom)}
                      localization={languageLocal.code}
                      closeOnMouseLeave={false}
                      dateTimeFormat={'DD.MM.YYYY HH:mm'}
                      value={watchDateTo}
                      iconPosition='left'
                      onChange={(event, { name, value }) => setValue('dateTo', value)}
                    />
                    {!watchDateTo && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter date from')}</StyledErrorLabel>}
                  </Form.Field>

                }}
              />
            </Form.Group>
            <Form.Field style={{ marginTop: '2em' }}>
              {watchLanguages?.length > 0 && renderLanguageTabs()}
            </Form.Field>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  }

  return <Layout
    segmented
    isBusy={isBusy || isLoading}
    disableSubmit={Object.keys(dirtyFields).length === 0}
    onSubmit={handleSubmit(onSubmit)}
    action={id ? t('Update') : t('Save')}
    close={RouteTypes.FAT_PROTEIN_PRICE}
    title={id ? t('Edit fat & protein trading slot') : t('Create fat & protein trading slot')}
  >
    {milkFixedCreateOptions && !isBusy && renderForm()}
  </Layout>
}
export default CreateEditFatAndProteinPrice
